import React, { useState } from "react";
import { customerAPI } from "../customerAPI";
import { api } from "../config";
import {
  clearPendingPayment,
  getPayPalOrderIdFromApprovalUrl,
  hasMatchingPendingPayment,
  storePendingPayment,
} from "../utils/paymentSession";

const PaymentModal = ({
  isOpen,
  onClose,
  isDarkMode,
  currentPlan = "free",
  remainingTokens = 10,
  totalTokens = 10,
  user,
  onPaymentSuccess,
  isUpgrade = false,
  activePlanVersionId = null,
}) => {
  const [selectedPlan, setSelectedPlan] = useState(null);

  const paymentSummaryRef = React.useRef(null);

  React.useEffect(() => {
    if (selectedPlan && paymentSummaryRef.current) {
      paymentSummaryRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [selectedPlan]);

  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [, setError] = useState(null);
  const [paymentOptions, setPaymentOptions] = useState([]);
  const [selectedGateway, setSelectedGateway] = useState("razorpay");
  const [, setRegion] = useState("GLOBAL");
  const [displayCurrency, setDisplayCurrency] = useState("USD");

  React.useEffect(() => {
    const fetchPlans = async () => {
      try {
        setLoading(true);
        const token = user?.token || user?.jwt || user?.userToken;
        const result = await customerAPI.getPlans(token);

        if (result.success) {
          const apiPlans = result.data.plans || [];
          setRegion(result.data.region || "GLOBAL");
          setDisplayCurrency(result.data.displayCurrency || "USD");

          // Override paymentOptions if USD
          let options = result.data.paymentOptions || [];
          if ((result.data.displayCurrency || "USD") === "USD") {
            options = [
              { gateway: "paypal", label: "PayPal" },
              { gateway: "razorpay", label: "Razorpay" }
            ];
          }
          setPaymentOptions(options);

          // Default to first gateway if available
          if (options.length > 0) {
            setSelectedGateway(options[0].gateway);
          } else {
            setSelectedGateway("razorpay");
          }

          // Map API data to component structure
          const mappedPlans = apiPlans.map(apiPlan => {
            const normalize = (str) => str?.toLowerCase().replace(/_/g, " ") || "";
            const name = normalize(apiPlan.planKey || apiPlan.plan); // e.g. "free plan", "market researcher"

            // Default styles - still hardcoded based on plan type as API doesn't provide UI styles
            let uiProps = {
              color: "gray",
              gradient: "from-gray-400 to-gray-500",
              popular: false,
              badge: ""
            };

            if (name.includes("free")) {
              uiProps = {
                color: "gray",
                gradient: "from-gray-400 to-gray-500",
                popular: false
              };
            } else if (name.includes("market") || name.includes("pro") || name.includes("researcher")) {
              uiProps = {
                color: "blue",
                gradient: "from-blue-500 to-blue-600",
                popular: true,
                savings: "Best Value"
              };
            } else {
              uiProps = {
                color: "purple",
                gradient: "from-purple-500 to-pink-500",
                badge: "Enterprise",
                popular: false
              };
            }

            // Extract pricing from nested object
            const pricing = apiPlan.pricing || {};

            // Robust parsing for diff regions
            const baseAmount = pricing.baseAmount !== undefined ? pricing.baseAmount : (pricing.base !== undefined ? pricing.base : (pricing.totalAmount !== undefined ? pricing.totalAmount : (pricing.total || 0)));
            const gstAmount = pricing.gstAmount !== undefined ? pricing.gstAmount : (pricing.gst || 0);
            const totalAmount = pricing.totalAmount !== undefined ? pricing.totalAmount : (pricing.total !== undefined ? pricing.total : (pricing.amount || 0));
            // Prioritize the top-level displayCurrency from the API (which indicates the regional currency) over the plan-specific hardcoded one
            const currency = result.data.displayCurrency || pricing.currency || "USD";

            let displayStr;
            // Use the API's provided formatted text if it exists (e.g "$100" or "₹100"),
            // otherwise format it manually based on the top-level currency.
            // Strip out GST text if present in the display
            const cleanDisplay = pricing.display ? pricing.display.replace(/\s*\(incl\.\s*\d+%\s*GST\)\s*/, '').trim() : null;
            
            if (cleanDisplay) {
              // If it's INR but the display string has a $, replace it. (Safety check)
              if (currency === "INR" && cleanDisplay.startsWith("$")) {
                displayStr = `₹${baseAmount.toLocaleString('en-IN')}`;
              } else {
                displayStr = cleanDisplay;
              }
            } else if (currency === "INR") {
              displayStr = `₹${baseAmount.toLocaleString('en-IN')}`;
            } else {
              displayStr = currency === "USD" ? `$${totalAmount}` : `${totalAmount} ${currency}`;
            }

            // Features from API
            const features = apiPlan.features || (apiPlan.description ? apiPlan.description.split(',').map(f => f.trim()) : []);

            // Determine precise display name
            let displayName = name.replace(/\b\w/g, l => l.toUpperCase());
            if (name.includes("free")) displayName = "FREE PLAN";
            else if (name.includes("market") || name.includes("researcher")) displayName = "MARKET RESEARCHER";
            else if (name.includes("cmo")) displayName = "IGR CREATIVES";

            // Assign sort order for consistent left-to-right display
            let sortOrder = 99;
            if (name.includes("free")) sortOrder = 1;
            else if (name.includes("market") || name.includes("researcher")) sortOrder = 2;
            else if (name.includes("cmo")) sortOrder = 3;

            const summaryDisplay = cleanDisplay || (currency === "INR" ? `₹${totalAmount.toLocaleString('en-IN')}` : (currency === "USD" ? `$${totalAmount}` : `${totalAmount} ${currency}`));

            const isFree = name.includes("free");

            return {
              id: apiPlan.versionId || apiPlan.planKey || apiPlan.plan,
              name: displayName,
              ...uiProps,
              price: displayStr, // Use the display string from API directly if possible, or formatted total
              basePrice: baseAmount,
              totalPrice: totalAmount,
              taxAmount: gstAmount,
              currency: currency,
              displayPrice: displayStr,
              summaryDisplay: summaryDisplay,
              tokens: apiPlan.credits, // "10" or "Unlimited"
              messagesPerDay: apiPlan.credits === "Unlimited" ? "Unlimited" : `~${Math.floor(Number(apiPlan.credits || 0) / 4)}`,
              features: features,
              limitations: [],
              taxLabel: gstAmount > 0 ? "(incl. 18% GST)" : "",
              rawTotalPrice: totalAmount,
              isFree: isFree,
              versionId: apiPlan.versionId,
              role: apiPlan.role,
              planKey: apiPlan.planKey,
              sortOrder,
              isCurrentAPI: apiPlan.isCurrent || false
            };
          });

          // Sort plans left-to-right: Free → Market Researcher → CMO Service
          mappedPlans.sort((a, b) => (a.sortOrder || 99) - (b.sortOrder || 99));

          setPlans(mappedPlans);
        } else {
          setError(result.error);
        }
      } catch (err) {
        setError("Failed to load plans");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    if (isOpen) {
      fetchPlans();
    }
  }, [isOpen, user]);

  const handleSelectPlan = (planId) => {
    const plan = plans.find(p => p.id === planId);
    if (plan && plan.isFree) return;
    if (plan && plan.name === "IGR CREATIVES") {

      return;
    }

    if (selectedPlan === planId) {
      paymentSummaryRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    setSelectedPlan(planId);
  };

  // Helper to load Razorpay SDK
  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const [paymentStatus, setPaymentStatus] = useState('idle'); // idle, processing, success, error
  const [statusMessage, setStatusMessage] = useState('');

  const handlePayment = async () => {
    if (!selectedPlan) return;

    // Flags to track payment outcome to avoid stale closure state in callbacks
    let isPaymentSuccess = false;
    let isPaymentFailed = false;

    try {
      const token = user?.token || user?.jwt;
      if (!token) {
        alert("Please login to proceed with payment.");
        return;
      }

      setPaymentStatus('processing');
      setStatusMessage('Initializing Payment...');

      const selectedPlanDetails = plans.find(p => p.id === selectedPlan);
      const currency = selectedPlanDetails?.currency || displayCurrency || "USD";

      // 1. Create Order with prorated upgrade endpoint for all plan changes
      const orderResult = await customerAPI.upgradeSubscription(token, selectedPlanDetails?.versionId, selectedGateway, currency);

      if (!orderResult.success) {
        setPaymentStatus('error');
        setStatusMessage(`Failed to process payment order: ` + orderResult.error);
        return;
      }

      console.log("Order created:", orderResult.data);
      const orderData = orderResult.data;

      // Handle PayPal Redirect
      if (orderData.gateway === 'paypal' && orderData.approvalUrl) {
        const paypalOrderId = getPayPalOrderIdFromApprovalUrl(orderData.approvalUrl);
        if (paypalOrderId) {
          storePendingPayment({ gateway: 'paypal', orderId: paypalOrderId, source: 'payment-modal-upgrade' });
        }
        setStatusMessage('Redirecting to PayPal...');
        window.location.href = orderData.approvalUrl;
        return;
      }

      // Handle Razorpay (Fallback/Standard)
      if (orderData.order) {
        const { id: order_id, amount, currency } = orderData.order;
        const razorpayOrderId = order_id;
        storePendingPayment({ gateway: 'razorpay', orderId: razorpayOrderId, source: 'payment-modal-upgrade' });

        // 2. Load Razorpay SDK
        const res = await loadRazorpayScript();
        if (!res) {
          setPaymentStatus('error');
          setStatusMessage("Razorpay SDK failed to load. Are you online?");
          return;
        }

        // 3. Initialize Razorpay Options
        const options = {
          key: api.razorpayKey,
          amount: amount,
          currency: currency || selectedPlanDetails?.currency || "USD",
          name: "Infinium Intelligence",
          description: `Upgrade to ${plans.find(p => p.id === selectedPlan)?.name}`,
          order_id: razorpayOrderId,
          handler: async function (response) {
            isPaymentSuccess = true;
            console.log("✅ Payment Success", response);
            setPaymentStatus('processing');
            setStatusMessage('Verifying Payment...');

            // 4. Verify Payment
            try {
              if (!hasMatchingPendingPayment({ gateway: 'razorpay', orderId: response.razorpay_order_id })) {
                setPaymentStatus('error');
                setStatusMessage('Payment verification rejected for this session.');
                return;
              }

              const verifyResult = await customerAPI.verifyPayment(token, {
                gateway: "razorpay",
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_order_id: response.razorpay_order_id,
                razorpay_signature: response.razorpay_signature
              });

              if (verifyResult.success) {
                console.log("✅ Verify result:", verifyResult.data);
                clearPendingPayment();
                setPaymentStatus('success');
                setStatusMessage('Payment Successful!');

                // Optional: Close modal after a delay
                // setTimeout(onClose, 3000); 

                // Refresh profile/plan in parent
                if (onPaymentSuccess) {
                  onPaymentSuccess();
                }

                // Force reload as requested
                window.location.reload();
              } else {
                setPaymentStatus('error');
                setStatusMessage("Payment verification failed: " + verifyResult.error);
              }
            } catch (err) {
              console.error("Verification error", err);
              setPaymentStatus('error');
              setStatusMessage("Payment verification failed during request.");
            }
          },
          prefill: {
            name: user?.name || user?.firstName || "",
            email: user?.email || "",
            contact: user?.phone || user?.contact || ""
          },
          theme: {
            color: "#2563eb"
          },
          modal: {
            ondismiss: function () {
              if (!isPaymentSuccess && !isPaymentFailed) {
                setPaymentStatus('idle');
              }
            }
          }
        };

        const rzp = new window.Razorpay(options);
        rzp.on('payment.failed', function (response) {
          isPaymentFailed = true;
          setPaymentStatus('error');
          setStatusMessage(response.error.description);
        });
        rzp.open();
      } else {
        throw new Error("Unknown payment gateway response");
      }

    } catch (err) {
      console.error("Payment error flow:", err);
      setPaymentStatus('error');
      setStatusMessage("An error occurred during payment initialization.");
    }
  };

  const getCurrentPlanTokens = () => {
    const normalizeId = (val) => {
      if (!val) return null;
      return String(val).toLowerCase().replace(/[\s_]+/g, "");
    };
    const normCurrent = normalizeId(currentPlan);
    const plan = plans.find((p) => {
      if (activePlanVersionId) {
        return p.versionId === activePlanVersionId;
      }
      if (p.isCurrentAPI) {
        return true;
      }
      if (normCurrent !== null) {
        if (p.isFree) return normCurrent.includes("free");
        return (
          normalizeId(p.id) === normCurrent ||
          normalizeId(p.name) === normCurrent ||
          normalizeId(p.versionId) === normCurrent ||
          normalizeId(p.role) === normCurrent ||
          normalizeId(p.planKey) === normCurrent
        );
      }
      return false;
    });
    return plan ? plan.tokens : 10;
  };

  const currentTokens = getCurrentPlanTokens();
  const tokenPercentage = currentTokens === "Unlimited"
    ? 100
    : Math.min(100, Math.max(0, (remainingTokens / (currentTokens || 1)) * 100));

  const getProgressColor = () => {
    if (tokenPercentage > 50) return "from-green-500 to-emerald-500";
    if (tokenPercentage > 20) return "from-yellow-500 to-orange-500";
    return "from-red-500 to-pink-500";
  };

  // Plan hierarchy: Free < Market Researcher < CMO Service
  const getPlanRank = (planName) => {
    const name = String(planName || "").toLowerCase();
    if (name.includes("free")) return 1;
    if (name.includes("market") || name.includes("researcher")) return 2;
    if (name.includes("cmo") || name.includes("service")) return 3;
    return 0;
  };

  // Resolves the active plan object the same way isCurrentPlan does, so rank checks never depend on the display name
  const findCurrentPlan = () => {
    const normalizeId = (val) => (val ? String(val).toLowerCase().replace(/[\s_]+/g, "") : null);
    const normCurrent = normalizeId(currentPlan);
    return plans.find((p) => {
      if (activePlanVersionId) return p.versionId === activePlanVersionId;
      if (p.isCurrentAPI) return true;
      if (normCurrent !== null) {
        if (p.isFree) return normCurrent.includes("free");
        return (
          normalizeId(p.id) === normCurrent ||
          normalizeId(p.name) === normCurrent ||
          normalizeId(p.versionId) === normCurrent ||
          normalizeId(p.role) === normCurrent ||
          normalizeId(p.planKey) === normCurrent
        );
      }
      return false;
    });
  };

  const getPlanActionLabel = (plan) => {
    const targetRank = getPlanRank(plan.planKey || plan.role || plan.name);
    const current = findCurrentPlan();
    const currentRank = getPlanRank(current?.planKey || current?.role || currentPlan);

    // Free plan card always shows "Free Plan" and is not selectable
    if (targetRank === 1) return "Free Plan";

    // Same plan → Current Plan
    if (targetRank === currentRank) return "Current Plan";

    // Upgrading from Free to any paid plan → Select Plan
    if (currentRank === 1) return "Select Plan";

    // Target is higher than current → Upgrade Plan
    if (targetRank > currentRank) return "Upgrade Plan";

    // Target is lower than current → Downgrade Plan
    if (targetRank < currentRank) return "Downgrade Plan";

    return "Select Plan";
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          className={`relative w-full max-w-5xl rounded-2xl shadow-2xl ${isDarkMode ? "bg-gray-900" : "bg-white"
            } transition-all transform my-8 scale-100 md:scale-95`}
        >
          {/* Close Button */}
          <button
            onClick={onClose}
            className={`absolute top-4 right-4 p-2 rounded-full transition-colors ${isDarkMode
              ? "hover:bg-gray-800 text-gray-400"
              : "hover:bg-gray-100 text-gray-600"
              }`}
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>

          {/* Header */}
          <div className="text-center pt-6 pb-4 px-6">
            <h2
              className={`text-2xl font-bold mb-2 ${isDarkMode ? "text-white" : "text-gray-900"
                }`}
            >
              Choose Your Plan
            </h2>
            <p
              className={`text-base ${isDarkMode ? "text-gray-400" : "text-gray-600"
                }`}
            >
              Unlock unlimited AI conversations and premium features
            </p>
          </div>

          {/* Loading State */}
          {loading ? (
            <div className="flex flex-col items-center justify-center p-12">
              <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mb-4"></div>
              <p className={`text-lg font-medium ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}>
                Loading plans...
              </p>
            </div>
          ) : (
            <>
              {/* Plans Grid */}
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 px-6 pb-6">
                {plans.map((plan) => {
                  const normalizeId = (val) => {
                    if (!val) return null;
                    return String(val).toLowerCase().replace(/[\s_]+/g, "");
                  };
                  const normCurrent = normalizeId(currentPlan);
                  let isCurrentPlan = false;
                  if (activePlanVersionId) {
                    isCurrentPlan = plan.versionId === activePlanVersionId;
                  } else if (plan.isCurrentAPI) {
                    isCurrentPlan = true;
                  } else if (normCurrent !== null) {
                    if (plan.isFree) {
                      isCurrentPlan = normCurrent.includes("free") && !activePlanVersionId;
                    } else if (!activePlanVersionId) {
                      isCurrentPlan =
                        normalizeId(plan.id) === normCurrent ||
                        normalizeId(plan.name) === normCurrent ||
                        normalizeId(plan.versionId) === normCurrent ||
                        normalizeId(plan.role) === normCurrent ||
                        normalizeId(plan.planKey) === normCurrent;
                    }
                  }
                  // console.log("Plan evaluation:", plan.name, "isCurrentPlan =", isCurrentPlan, "normCurrent =", normCurrent, "planId =", normalizeId(plan.id), "planVer =", normalizeId(plan.versionId), "planRole=", normalizeId(plan.role));
                  const showTokenInfo = isCurrentPlan;

                  // Detect if user is on a higher plan than Market Researcher — resolve the active plan and check its canonical key, not the display name
                  const resolvedCurrentPlan = findCurrentPlan();
                  const currentCanonical = (resolvedCurrentPlan?.planKey || resolvedCurrentPlan?.role || currentPlan || "").toLowerCase();
                  const isOnHigherPlan = currentCanonical.includes("cmo") || currentCanonical.includes("enterprise");
                  // Suppress "Best Value" badge when user is already on a higher plan
                  const showBestValue = plan.popular && !isOnHigherPlan;
                  // Blue border on current plan, not on "popular" plan
                  const isHighlighted = isCurrentPlan;

                  return (
                    <div
                      key={plan.id}
                      className={`relative flex flex-col rounded-2xl p-5 transition-all cursor-pointer ${selectedPlan === plan.id
                        ? `ring-4 ring-green-500 ${isDarkMode ? "bg-gray-800" : "bg-gray-50"
                        }`
                        : isDarkMode
                          ? "bg-gray-800 hover:bg-gray-750"
                          : "bg-gray-50 hover:bg-gray-100"
                        } ${isHighlighted ? "border-2 border-blue-500" : ""} ${plan.isFree ? "opacity-75 cursor-not-allowed" : ""
                        }`}
                      onClick={() => !plan.isFree && handleSelectPlan(plan.id)}
                    >
                      {/* Popular / Best Value Badge — hidden when user is on higher plan */}
                      {showBestValue && (
                        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                          <span className="px-4 py-1 rounded-full text-xs font-bold text-white bg-gradient-to-r from-blue-500 to-blue-600 shadow-lg">
                            {plan.savings}
                          </span>
                        </div>
                      )}

                      {isCurrentPlan ? (
                        <div className="absolute -top-3 right-4">
                          <span className="px-3 py-1 rounded-full text-xs font-bold text-white bg-gradient-to-r from-green-500 to-emerald-600 shadow-lg border border-green-400">
                            Current Plan
                          </span>
                        </div>
                      ) : plan.badge ? (
                        <div className="absolute -top-3 right-4">
                          <span
                            className={`px-3 py-1 rounded-full text-xs font-bold text-white bg-gradient-to-r ${plan.gradient} shadow-lg`}
                          >
                            {plan.badge}
                          </span>
                        </div>
                      ) : null}

                      {/* Plan Name */}
                      <div className="mb-6">
                        <h3
                          className={`text-2xl font-bold mb-3 ${isDarkMode ? "text-white" : "text-gray-900"
                            }`}
                        >
                          {plan.name}
                        </h3>
                        <div>
                          <span
                            className={`text-4xl font-bold bg-gradient-to-r ${plan.gradient} bg-clip-text text-transparent`}
                          >
                            {plan.price}
                          </span>
                          {plan.basePrice > 0 && (
                            <div className="mt-2">
                              <div className={`text-sm font-semibold ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>
                                /month
                              </div>
                              {plan.taxLabel && (
                                <div className={`text-[10px] font-normal italic ${isDarkMode ? "text-gray-500" : "text-gray-500"}`}>
                                  {plan.taxLabel}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Current Plan - Show Token Usage */}
                      {showTokenInfo && (
                        <div
                          className={`mb-4 p-4 rounded-lg ${isDarkMode ? "bg-gray-900" : "bg-white"
                            } border-2 ${isDarkMode ? "border-cyan-500/30" : "border-cyan-500/50"
                            }`}
                        >
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-xl">⚡</span>
                            <span
                              className={`text-sm font-semibold ${isDarkMode ? "text-cyan-400" : "text-cyan-600"
                                }`}
                            >
                              Your Current Plan
                            </span>
                          </div>

                          <div className="flex items-center justify-between mb-2">
                            <span
                              className={`text-xs font-medium ${isDarkMode ? "text-gray-400" : "text-gray-600"
                                }`}
                            >
                              Remaining Tokens
                            </span>
                            <span
                              className={`text-lg font-bold ${isDarkMode ? "text-white" : "text-gray-900"
                                }`}
                            >
                              {getCurrentPlanTokens() === "Unlimited"
                                ? "Unlimited"
                                : `${remainingTokens.toLocaleString()} / ${getCurrentPlanTokens()}`}
                            </span>
                          </div>

                          {/* Progress Bar */}
                          <div
                            className={`w-full h-2 rounded-full overflow-hidden ${isDarkMode ? "bg-gray-700" : "bg-gray-200"
                              }`}
                          >
                            <div
                              className={`h-full bg-gradient-to-r ${getProgressColor()} transition-all duration-300`}
                              style={{ width: `${tokenPercentage}%` }}
                            />
                          </div>
                        </div>
                      )}

                      {/* Tokens - Only show for non-current plans */}
                      {!showTokenInfo && (
                        <div
                          className={`mb-4 p-3 rounded-lg ${isDarkMode ? "bg-gray-900" : "bg-white"
                            }`}
                        >
                          <div className="flex items-center justify-between">
                            <span
                              className={`text-sm font-medium ${isDarkMode ? "text-gray-400" : "text-gray-600"
                                }`}
                            >
                              Tokens
                            </span>
                            <span
                              className={`text-xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"
                                }`}
                            >
                              {plan.tokens}
                            </span>
                          </div>
                          <div className="flex items-center justify-between mt-1">
                            <span
                              className={`text-xs ${isDarkMode ? "text-gray-500" : "text-gray-500"
                                }`}
                            >
                              Messages/day
                            </span>
                            <span
                              className={`text-sm font-semibold ${isDarkMode ? "text-gray-300" : "text-gray-700"
                                }`}
                            >
                              {plan.messagesPerDay}
                            </span>
                          </div>
                        </div>
                      )}

                      {/* Features */}
                      <ul className="space-y-3 mb-6">
                        {plan.features.map((feature, idx) => (
                          <li key={idx} className="flex items-start gap-2">
                            <svg
                              className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M5 13l4 4L19 7"
                              />
                            </svg>
                            <span
                              className={`text-sm ${isDarkMode ? "text-gray-300" : "text-gray-700"
                                }`}
                            >
                              {feature}
                            </span>
                          </li>
                        ))}
                      </ul>

                      {/* Limitations */}
                      {plan.limitations.length > 0 && (
                        <ul className="space-y-2 mb-6">
                          {plan.limitations.map((limitation, idx) => (
                            <li key={idx} className="flex items-start gap-2">
                              <svg
                                className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M6 18L18 6M6 6l12 12"
                                />
                              </svg>
                              <span
                                className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-600"
                                  }`}
                              >
                                {limitation}
                              </span>
                            </li>
                          ))}
                        </ul>
                      )}

                      {/* Action Button */}
                      <div className="mt-auto pt-6">
                        {selectedPlan === plan.id && !isCurrentPlan ? (
                          <button
                            onClick={() => handleSelectPlan(plan.id)}
                            className={`w-full py-3 rounded-xl font-semibold text-white transition-all transform hover:scale-105 bg-gradient-to-r from-green-500 to-green-600 shadow-lg`}
                          >
                            Selected
                          </button>
                        ) : (
                          <button
                            onClick={() => !plan.isFree && handleSelectPlan(plan.id)}
                            disabled={plan.isFree || isCurrentPlan}
                            className={`w-full py-3 rounded-xl font-semibold transition-all transform hover:scale-105 ${
                              plan.isFree || isCurrentPlan
                                ? isDarkMode
                                  ? "bg-gray-700 text-gray-400 cursor-not-allowed"
                                  : "bg-gray-200 text-gray-500 cursor-not-allowed"
                                : `bg-gradient-to-r ${plan.gradient} text-white opacity-80 hover:opacity-100`
                            }`}
                          >
                            {getPlanActionLabel(plan)}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}

              </div>

              {/* Status Overlay */}
              {paymentStatus !== 'idle' && (
                <div className="absolute inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70 rounded-2xl backdrop-blur-sm transition-all duration-300">
                  <div className={`p-8 rounded-2xl shadow-2xl flex flex-col items-center justify-center max-w-sm w-full mx-4 transform transition-all scale-100 ${isDarkMode ? "bg-gray-800" : "bg-white"}`}>

                    {/* Processing State */}
                    {paymentStatus === 'processing' && (
                      <>
                        <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mb-4"></div>
                        <h3 className={`text-xl font-bold mb-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}>Processing Payment</h3>
                        <p className={`text-center ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>{statusMessage || "Please wait..."}</p>
                      </>
                    )}

                    {/* Success State */}
                    {paymentStatus === 'success' && (
                      <>
                        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-4 animate-[bounce_1s_ease-in-out]">
                          <svg className="w-10 h-10 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                        <h3 className={`text-2xl font-bold mb-2 text-green-500`}>Success!</h3>
                        <p className={`text-center mb-6 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>{statusMessage}</p>
                        <button
                          onClick={onClose}
                          className="px-6 py-2 bg-green-500 text-white rounded-lg font-semibold hover:bg-green-600 transition-colors"
                        >
                          Done
                        </button>
                      </>
                    )}

                    {/* Error State */}
                    {paymentStatus === 'error' && (
                      <>
                        <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mb-4">
                          <svg className="w-10 h-10 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </div>
                        <h3 className={`text-2xl font-bold mb-2 text-red-500`}>Failed</h3>
                        <p className={`text-center mb-6 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>{statusMessage}</p>
                        <div className="flex gap-3">
                          <button
                            onClick={() => setPaymentStatus('idle')}
                            className={`px-4 py-2 rounded-lg font-semibold transition-colors ${isDarkMode ? "bg-gray-700 text-gray-300 hover:bg-gray-600" : "bg-gray-200 text-gray-600 hover:bg-gray-300"}`}
                          >
                            Cancel
                          </button>
                          <button
                            onClick={() => { setPaymentStatus('idle'); handlePayment(); }}
                            className="px-4 py-2 bg-blue-500 text-white rounded-lg font-semibold hover:bg-blue-600 transition-colors"
                          >
                            Try Again
                          </button>
                        </div>
                      </>
                    )}

                  </div>
                </div>
              )}

              {/* Payment Button & Summary - Redesigned */}
              {selectedPlan && paymentStatus === 'idle' && (
                <div
                  ref={paymentSummaryRef}
                  className={`border-t ${isDarkMode ? "border-gray-800" : "border-gray-200"
                    } p-6`}
                >
                  <div className="flex flex-col md:flex-row items-center justify-between max-w-5xl mx-auto gap-6 transition-all">

                    <div className="flex-1">
                      <p
                        className={`text-sm font-medium mb-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"
                          }`}
                      >
                        You are choosing
                      </p>
                      <div className="flex items-baseline gap-2">
                        <p
                          className={`text-2xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"
                            }`}
                        >
                          {plans.find((p) => p.id === selectedPlan)?.name}
                        </p>
                        <span className={`text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>•</span>
                        <p className={`text-xs font-medium ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                          {plans.find((p) => p.id === selectedPlan)?.summaryDisplay}
                        </p>
                      </div>
                    </div>

                    <div className="flex flex-col gap-4 w-full md:w-auto items-end">
                      {/* Payment Gateway Selection */}
                      {paymentOptions.length > 1 && (
                        <div className="flex flex-col gap-2 w-full">
                          <label className={`text-xs font-semibold uppercase tracking-wider ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
                            Select Payment Method
                          </label>
                          <div className="grid grid-cols-2 gap-3">
                            {paymentOptions.map((opt) => (
                              <button
                                key={opt.gateway}
                                onClick={() => setSelectedGateway(opt.gateway)}
                                className={`relative group flex flex-col items-center justify-center p-3 rounded-xl border-2 transition-all duration-200 ${selectedGateway === opt.gateway
                                  ? isDarkMode
                                    ? "bg-blue-900/20 border-blue-500"
                                    : "bg-blue-50 border-blue-500"
                                  : isDarkMode
                                    ? "bg-gray-800 border-gray-700 hover:border-gray-600"
                                    : "bg-white border-gray-200 hover:border-gray-300"
                                  }`}
                              >
                                {/* Setup Icons */}
                                {opt.gateway === 'paypal' ? (
                                  <div className="h-6 flex items-center mb-1">
                                    {/* PayPal Text Logo Style */}
                                    <span className="text-xl font-bold italic text-[#003087]">Pay</span>
                                    <span className="text-xl font-bold italic text-[#009cde]">Pal</span>
                                  </div>
                                ) : (
                                  <div className="h-6 flex items-center mb-1 gap-1.5">
                                    {/* Razorpay Logo Style - Stronger Contrast */}
                                    <div className="flex items-center gap-1.5">
                                      <div className="bg-[#0c2444] rounded-full p-0.5">
                                        <svg className="w-4 h-4 text-white" viewBox="0 0 24 24" fill="currentColor">
                                          <path d="M12.8 12L16 2.5L2 9.4L11.2 12L8 21.5L22 14.6L12.8 12Z" />
                                        </svg>
                                      </div>
                                      <span className={`text-lg font-bold ${isDarkMode ? "text-white" : "text-[#0c2444]"}`}>Razorpay</span>
                                    </div>
                                  </div>
                                )}

                                {/* Checkmark for selected state */}
                                {selectedGateway === opt.gateway && (
                                  <div className="absolute top-1.5 right-1.5">
                                    <div className="w-4 h-4 rounded-full bg-blue-500 flex items-center justify-center">
                                      <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                      </svg>
                                    </div>
                                  </div>
                                )}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      <button
                        onClick={handlePayment}
                        disabled={!selectedGateway}
                        className={`w-full px-8 py-4 rounded-xl font-bold text-white transition-all transform hover:scale-[1.02] active:scale-95 shadow-xl ${!selectedGateway
                          ? "bg-gray-400 cursor-not-allowed"
                          : "bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 hover:shadow-blue-500/25"
                          }`}
                      >
                        {selectedGateway === 'paypal' ? 'Pay with PayPal' : selectedGateway === 'razorpay' ? 'Pay with Razorpay' : 'Proceed to Payment'}
                      </button>
                    </div>
                  </div>
                </div>
              )}

            </>
          )}

          {/* Footer */}
          <div
            className={`border-t ${isDarkMode ? "border-gray-800" : "border-gray-200"
              } p-4`}
          >
            <p
              className={`text-center text-xs ${isDarkMode ? "text-gray-500" : "text-gray-500"
                }`}
            >
              🔒 Secure payment powered by Razorpay • Cancel anytime •
              Money-back guarantee
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentModal;